import type { ReactNode } from 'react';
import { cn } from './cn.js';

export type BadgeTone = 'neutral' | 'go' | 'bee' | 'vir' | 'danger' | 'info';

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  dot?: boolean;
  className?: string;
}

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-sunken text-ink-muted',
  go: 'bg-go-light text-go-dark',
  bee: 'bg-bee-light text-bee-dark',
  vir: 'bg-vir-light text-vir',
  danger: 'bg-danger-light text-danger',
  info: 'bg-info-light text-info',
};

const DOTS: Record<BadgeTone, string> = {
  neutral: 'bg-ink-soft',
  go: 'bg-go',
  bee: 'bg-bee',
  vir: 'bg-vir',
  danger: 'bg-danger',
  info: 'bg-info',
};

export function Badge({ children, tone = 'neutral', dot = false, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2 py-0.5 text-xs font-semibold',
        TONES[tone],
        className,
      )}
    >
      {dot && <span className={cn('h-1.5 w-1.5 rounded-full', DOTS[tone])} />}
      {children}
    </span>
  );
}

/** Lead stages carry a consistent tone across every screen that shows them. */
export const STAGE_TONES: Record<string, BadgeTone> = {
  NEW: 'neutral',
  CONTACTED: 'info',
  QUALIFIED: 'vir',
  PROPOSAL: 'bee',
  NEGOTIATION: 'bee',
  WON: 'go',
  LOST: 'danger',
};

export const TEMPERATURE_TONES: Record<string, BadgeTone> = {
  COLD: 'info',
  WARM: 'bee',
  HOT: 'danger',
};
